import {memo, useCallback, useEffect, useRef, useState} from 'react';

import styled from 'styled-components';
import {debounce} from 'lodash';

import {getLocalizationService} from '~/shared/services/localisationService';
import {flipOnLTR} from '~/shared/theme/utils';
import {getCurrentGeolocationPlace} from '~/shared/services/geocoder';
import AddressAutoComplete from '~/shared/components/CustomAddressAutoComplete';
import {createLogger} from '~/shared/logging';
import getAddressFinderAddress from '~/shared/components/AddressFinder/getAddressFinderAddress';
import {getIsKeyboardInUse} from '~/shared/usingKeyboard/keyboardHelper';
import {body16Normal} from '~/shared/theme/typography';
import {trackEvent} from '~/shared/services/analytics';

const logger = createLogger('AddressFinder');

const Root = styled.div`
  display: flex;
  flex: 1 1 auto;
`;

const HouseNumberInput = styled.input`
  width: 56px;
  height: 100%;
  border: 0;
  font-size: ${({isAddAddressMode}) => (isAddAddressMode ? 16 : 18)}px;
  text-align: center;
  color: ${({theme}) => theme.colors.secondary};

  ::placeholder {
    color: ${({theme}) => theme.colors.secondary};
  }

  ${flipOnLTR`
    border-right: 1px solid ${({theme}) => theme.colors.background};
  `}
`;

const ErrorIndicatorMobileAndDropDown = styled.div.attrs({
  name: 'errorIndicator',
  role: 'alert',
})`
  z-index: 3;
  color: ${({theme}) => theme.colors.error};
  border: 1px solid ${({theme}) => theme.colors.error};
  background: ${({theme}) => theme.colors.surface};
  padding: 12px;
  top: calc(100% + 16px);
  ${body16Normal}
  position: absolute;
  ${flipOnLTR`
    left: 16px;
  `}

  &::after {
    content: '';
    z-index: 1;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid ${({theme}) => theme.colors.error};
    position: absolute;
    top: -8px;
    ${flipOnLTR`
      left: 4px;
    `}
  }

  &::before {
    content: '';
    z-index: 2;
    width: 0;
    height: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-bottom: 7px solid ${({theme}) => theme.colors.surface};
    position: absolute;
    top: -7px;
    ${flipOnLTR`
      left: 5px;
    `}
  }
`;

const AddressFinder = ({
  location,
  placeholder,
  houseNumberRef,
  itemHeight,
  isAddAddressMode,
  onChange,
  setDisableSubmit,
  disableSubmit,
  submit: submitForm,
  isMaxMobile,
  disableAutoFocus,
}) => {
  const {t} = getLocalizationService();

  const isFirstRender = useRef(true);
  const rootRef = useRef(null);

  const [customHouseNumber, setCustomHouseNumber] = useState();
  const [showAddressNumberInput, setShowAddressNumberInput] = useState();
  const [currentPlace, setCurrentPlace] = useState();
  const [errorMessage, setErrorMessage] = useState('');

  const debouncedSetCustomHouseNumber = debounce(value => {
    setCustomHouseNumber(value);
  }, 300);

  const handleCustomHouseNumberChange = useCallback(value => {
    debouncedSetCustomHouseNumber(value);
  }, [debouncedSetCustomHouseNumber]);

  const onPlaceSelected = useCallback(
    async place => {
      if (!place || !place.place_id) {
        setErrorMessage(t('unexpected_error'));
        return;
      }

      setCurrentPlace(place);

      const addressRes = await getAddressFinderAddress(customHouseNumber, place);
      if ('error' in addressRes) {
        const {error: addressError} = addressRes;
        switch (addressError.type) {
          case 'missing':
            setErrorMessage(t('missing_xxx_please_fill_xxx', {field: t(addressError.field)}));
            break;
          case 'unexpected':
            setErrorMessage(t('unexpected_error'));
            break;
          case 'missing_houseNumber':
            setDisableSubmit(true);
            setShowAddressNumberInput(true);
            setErrorMessage(t('missing_xxx_please_fill_xxx', {field: t(addressError.field)}));
            break;
          default:
            logger.warn('unhandled error');
        }

        return;
      }

      const {data: address} = addressRes;
      setErrorMessage(null);
      setDisableSubmit(false);

      onChange(address);

      if (location) {
        trackEvent('hasModifiedAddress', {
          modifiedAddressType: 'add address',
          modifiedLocation: location,
        });
      }

      if (!getIsKeyboardInUse()) {
        submitForm(address);
      }
    },
    [customHouseNumber, setDisableSubmit, onChange, location, t, submitForm],
  );

  const addressSelectorRef = useCallback(domEl => {
    if (domEl && !disableAutoFocus) {
      domEl.focus();
    }
  }, [disableAutoFocus]);

  useEffect(() => {
    const keyEventsHandler = event => {
      if (event.key === 'Enter' && !disableSubmit) {
        submitForm();
      }
    };
    rootRef.current?.addEventListener('keydown', keyEventsHandler, false);

    const refForCleanup = rootRef.current;
    return () => refForCleanup?.removeEventListener('keydown', keyEventsHandler, false);
  }, [addressSelectorRef, disableSubmit, rootRef, submitForm]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      onPlaceSelected(currentPlace);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customHouseNumber]);

  useEffect(() => {
    if (!getIsKeyboardInUse() && showAddressNumberInput && houseNumberRef && houseNumberRef.current) {
      houseNumberRef.current.focus();
    }
  }, [houseNumberRef, showAddressNumberInput]);

  const resetAddress = () => {
    setDisableSubmit(true);
    setErrorMessage(null);
    setShowAddressNumberInput(false);
    setCurrentPlace('');
    onChange(null);
  };

  const getCurrentLocation = async ({setAddress}) => {
    try {
      const place = await getCurrentGeolocationPlace();

      resetAddress();
      setAddress(place.name);
      onPlaceSelected(place);

      trackEvent('addressStartSearch', {
        searchOption: 'location_based',
      });
    } catch (err) {
      // ignore- failing getting the initial device address will just leave the address input empty.
      console.warn('Cant get initial location', err); // eslint-disable-line no-console
    }
  };

  useEffect(() => {
    if (!errorMessage) {
      return;
    }

    trackEvent('formErrorMessageView', {
      errorType: errorMessage,
      formName: 'address',
    });
  }, [errorMessage]);

  return (
    <Root ref={rootRef}>
      <AddressAutoComplete
        inputRef={addressSelectorRef}
        onCurrectLocationClick={getCurrentLocation}
        {...{placeholder, onPlaceSelected, isMaxMobile, resetAddress, itemHeight, isAddAddressMode, location}}
      />
      {errorMessage && <ErrorIndicatorMobileAndDropDown>{errorMessage}</ErrorIndicatorMobileAndDropDown>}
      {showAddressNumberInput && (
        <HouseNumberInput
          placeholder={t('house')}
          ref={houseNumberRef}
          initialValue=""
          onChange={e => handleCustomHouseNumberChange(e.target.value)}
          maxLength={5}
          isAddAddressMode={isAddAddressMode}
        />
      )}
    </Root>
  );
};

export default memo(AddressFinder);
